import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

import { authAPI, guidedFlowAPI } from '../../data/auth';
import { invalidateGuidedFlow } from '../../features/guidedFlow/queries';
import pt from '../../locales/pt';
import {
  apiErrorMessage,
  clampSelfieDay,
} from '../../utils/guidedFlowUtils';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

export function GuidedFlowSelfieDayStep({
  initialDay,
  continueLabel,
  onContinue,
  isAdvancing,
}) {
  const qc = useQueryClient();
  const [day, setDay] = useState(() => clampSelfieDay(initialDay ?? 28));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialDay != null) setDay(clampSelfieDay(initialDay));
  }, [initialDay]);

  const handleContinue = async () => {
    const normalized = clampSelfieDay(day);
    setDay(normalized);
    setSaving(true);
    try {
      const profileRes = await authAPI.patchProfile({
        profile: { selfie_day_of_month: normalized },
      });
      if (!profileRes.success) {
        toast.error(
          apiErrorMessage(
            profileRes.error,
            'Não foi possível guardar o dia da selfie.',
          ),
        );
        return;
      }

      const seenRes = await guidedFlowAPI.markWizardSeen('selfie_day_setup');
      if (seenRes.success) {
        invalidateGuidedFlow(qc);
      }
      if (!seenRes.success) {
        toast.error(
          apiErrorMessage(
            seenRes.error,
            'Não foi possível concluir este passo.',
          ),
        );
        return;
      }

      await onContinue();
      toast.success(pt.dashboard.guidedFlow.selfieDay.savedToast);
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || isAdvancing;

  return (
    <div className="flex flex-col gap-space-2 pt-space-2">
      <div>
        <label
          htmlFor="guided-selfie-day"
          className="text-type-body-sm font-semibold text-foreground"
        >
          {pt.dashboard.guidedFlow.selfieDay.label}
        </label>
        <p className="mt-1 type-label-meta leading-snug text-muted-foreground">
          {pt.dashboard.guidedFlow.selfieDay.help}
        </p>
        <p className="mt-space-2 type-label-meta leading-snug text-sage-on-tint">
          {pt.dashboard.guidedFlow.selfieDay.salaryTip}
        </p>
      </div>
      <div className="flex items-center gap-space-2 pt-1">
        <Input
          id="guided-selfie-day"
          type="number"
          min={1}
          max={31}
          inputMode="numeric"
          value={day}
          onChange={(event) => setDay(clampSelfieDay(event.target.value))}
          className="w-[76px] px-space-3 py-space-2 text-center font-body tabular-nums"
          aria-describedby="guided-selfie-day-hint"
        />
        <span id="guided-selfie-day-hint" className="text-type-body-sm text-muted-foreground">
          {pt.dashboard.guidedFlow.selfieDay.daySuffix}
        </span>
      </div>
      <Button
        type="button"
        onClick={handleContinue}
        disabled={busy}
        className="mt-space-1 w-full rounded-ds-md text-type-body-sm font-bold"
      >
        {continueLabel}
        <ArrowRight className="h-[18px] w-[18px]" weight="bold" aria-hidden />
      </Button>
    </div>
  );
}
